import type { ViewFieldMeta } from './viewTypes'

/**
 * AB#2518 — seeds fields from the record another field points at.
 *
 * A quote line's description comes from the chosen Material; a quote's currency comes from the chosen
 * Customer. Both are "when this entity-ref is chosen, fill that field from the referenced record, but
 * leave it editable afterwards".
 */

/** Fetches the referenced record's stored fields. Host-provided: the designer preview wires nothing. */
export type FetchEntityRecord = (
    sourceEntity: string,
    id: string,
    companyId?: string,
) => Promise<Record<string, unknown> | undefined>

const isBlank = (v: unknown): boolean =>
    v === null || v === undefined || (typeof v === 'string' && v.trim() === '')

/**
 * AB#3113 — a field's copyFrom declaration is one spec or a list of them (a field with two possible
 * master sources — a delivery partner's address from the customer OR the vendor the row names —
 * declares one spec per reference). Every reader goes through this normaliser so the two shapes
 * cannot behave differently.
 */
export function copyFromSources(m: ViewFieldMeta): { ref: string; field: string }[] {
    if (!m.copyFrom) { return [] }
    return Array.isArray(m.copyFrom) ? m.copyFrom : [m.copyFrom]
}

/**
 * Which fields a change to `changedField` should seed, and from where.
 * Exported for the tests and for a host that wants to pre-resolve the fetches.
 */
export function copyTargetsFor(changedField: string, meta: readonly ViewFieldMeta[]): ViewFieldMeta[] {
    return meta.filter(m => copyFromSources(m).some(s => s.ref === changedField))
}

/**
 * Returns the patch to apply to `record` after `changedField` was set to `newRefValue`.
 *
 * The rule for when a value may be replaced is the whole design:
 *
 * - **Blank** — always seeded. Nothing is lost.
 * - **Still holding what we last seeded** — replaced. Picking material A then material B must not
 *   leave A's description sitting under B, which is worse than either seeding or not seeding.
 * - **Anything else** — left alone. The user typed it, and a re-seed must never overwrite that.
 *
 * `lastSeeded` carries the previous seeded value per field so the middle case can be told from the
 * third; a caller that keeps no such record simply loses that case and never overwrites.
 *
 * Clearing the reference clears nothing: a blanked target would look like data loss, and the previous
 * value is still the honest answer until something replaces it.
 */
export async function applyCopyFrom(
    record: Record<string, unknown>,
    changedField: string,
    newRefValue: unknown,
    meta: readonly ViewFieldMeta[],
    fetchRecord: FetchEntityRecord | undefined,
    lastSeeded: Record<string, unknown>,
    companyId?: string,
    getCurrentRecord?: () => Record<string, unknown> | undefined,
): Promise<Record<string, unknown>> {
    const targets = copyTargetsFor(changedField, meta)
    if (targets.length === 0 || !fetchRecord) { return {} }
    if (isBlank(newRefValue) || typeof newRefValue !== 'string') { return {} }

    const sourceEntity = meta.find(m => m.name === changedField)?.lookupField?.sourceEntity
    if (!sourceEntity) { return {} }

    let source: Record<string, unknown> | undefined
    try {
        source = await fetchRecord(sourceEntity, newRefValue, companyId)
    } catch {
        // A seed is a convenience. Failing the edit because the lookup was unreachable would be worse
        // than leaving the field for the user to type.
        return {}
    }
    if (!source) { return {} }

    // AB#3700 — a lookup may finish after another selection, a manual edit, or
    // removal of the row. Resolve the same live record before changing any seeds.
    const currentRecord = getCurrentRecord ? getCurrentRecord() : record
    if (!currentRecord || (getCurrentRecord && currentRecord[changedField] !== newRefValue)) { return {} }

    const patch: Record<string, unknown> = {}
    for (const target of targets) {
        // AB#3113 — the spec matching the reference that CHANGED is the one that seeds; the field's
        // other sources belong to references this edit did not touch.
        const spec = copyFromSources(target).find(s => s.ref === changedField)
        if (!spec) { continue }
        const value = source[spec.field]
        // A missing or null source value leaves the target untouched rather than blanking it.
        if (isBlank(value)) { continue }

        const current = currentRecord[target.name]
        if (getCurrentRecord && !Object.is(current, record[target.name])) { continue }
        const replaceable = isBlank(current) || current === lastSeeded[target.name]
        if (!replaceable) { continue }

        patch[target.name] = value
        lastSeeded[target.name] = value
    }
    return patch
}
